import React, { useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Alert,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from 'reactstrap'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import AlertMessage from '../../components/AlertMessage'
import PageLoader from '../../components/PageLoader'
import projectsActions from './actions'

const ProjectForceReImportConfirmModal = ({
  isOpen,
  onClose,
  project = {}
}) => {
  const { t } = useTranslation(['ProjectsPage', 'commons'])
  const dispatch = useDispatch()

  const {
    creationError = null,
    creationStatus = 'idle',
    removingError = null,
    removingStatus = 'idle'
  } = useSelector((state) => state.projects)

  const integrations = useSelector((state) => state.integrations.items)

  const [confirmationName, setConfirmationName] = useState('')
  const [processing, setProcessing] = useState(false)
  const [removalStarted, setRemovalStarted] = useState(false)
  const [awaitingCreation, setAwaitingCreation] = useState(false)
  const projectSettingsRef = useRef(null)

  // Track when removal actually starts (status becomes 'removing')
  if (processing && !removalStarted && removingStatus === 'removing') {
    setRemovalStarted(true)
  }

  // When removal succeeds (after it started), create the new project
  if (removalStarted
      && removingStatus === 'succeeded'
      && projectSettingsRef.current) {
    setRemovalStarted(false)
    setAwaitingCreation(true)
    dispatch(projectsActions.create(projectSettingsRef.current))
  }

  // When creation succeeds and we're waiting for it, close the modal
  if (awaitingCreation && creationStatus === 'succeeded') {
    setAwaitingCreation(false)
    setProcessing(false)
    projectSettingsRef.current = null
    onClose()
  }

  const handleSubmit = (event) => {
    event.preventDefault()

    if (confirmationName !== project.name) return

    // Store project settings before deletion
    const paths = Array.isArray(project.paths)
      ? project.paths.join(', ')
      : (project.paths || '')

    const { platform } = integrations[0]

    projectSettingsRef.current = {
      branch_name: project.repository_branch_name,
      locales: project.reference_languages,
      name: project.name,
      paths,
      platform,
      repository_id: project.repository_id
    }

    setProcessing(true)
    dispatch(projectsActions.remove(project.id))
  }

  const isConfirmDisabled = () => confirmationName !== project?.name || processing

  const i18nBase = 'ProjectForceReImportConfirmModal'
  const error = removingError || creationError

  return (
    <Modal isOpen={isOpen} toggle={() => onClose()}>
      <Form>
        <ModalHeader toggle={() => onClose()}>
          {t(`${i18nBase}.title`)}
        </ModalHeader>
        <ModalBody>
          <Alert color="danger">
            <strong>{t(`${i18nBase}.warning`)}</strong>
          </Alert>

          <p className="lead">{t(`${i18nBase}.headerMessage`)}</p>
          <p>{t(`${i18nBase}.bodyMessage`)}</p>

          <FormGroup className="mt-4">
            <Label for="confirmationName">
              {t(`${i18nBase}.confirmationLabel`)}
            </Label>
            <Input
              id="confirmationName"
              name="confirmationName"
              onChange={({ target: { value } }) => setConfirmationName(value)}
              placeholder={project?.name}
              type="text"
              value={confirmationName}
            />
          </FormGroup>

          {processing && (
            <PageLoader message={t(`${i18nBase}.processing`)} />
          )}

          {error && (
            <AlertMessage>
              {typeof error === 'string'
                ? error
                : t(`${i18nBase}.errors.reImportFailed`)}
            </AlertMessage>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => onClose()}>
            {t('buttons.cancel', { ns: 'commons' })}
          </Button>
          <Button
            color="danger"
            disabled={isConfirmDisabled()}
            onClick={(event) => handleSubmit(event)}
            type="submit"
          >
            {t('buttons.confirm', { ns: 'commons' })}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

ProjectForceReImportConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  project: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    paths: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string)
    ]),
    platform: PropTypes.string,
    reference_languages: PropTypes.arrayOf(PropTypes.string),
    repository_branch_name: PropTypes.string,
    repository_id: PropTypes.number
  })
}

export default ProjectForceReImportConfirmModal
